// IndexedDB utility for article history management

export interface ArticleHistoryItem {
    id: string;
    title: string;
    url: string;
    content: string; // First 100 characters
    date: Date;
    userAgent: string;
    bypassPaywall?: boolean;
}

const DB_NAME = 'article-viewer-history';
const DB_VERSION = 1;
const STORE_NAME = 'articles';

class ArticleHistoryDB {
    private db: IDBDatabase | null = null;

    init(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                    store.createIndex('date', 'date', { unique: false });
                }
            };
        });
    }

    async addArticle(article: Omit<ArticleHistoryItem, 'id'>): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            const articleWithId: ArticleHistoryItem = {
                ...article,
                id: `${article.url}-${Date.now()}`
            };

            const request = store.add(articleWithId);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                // Keep only the last 20 articles
                this.limitArticles(20);
                resolve();
            };
        });
    }

    async updateOrAddArticle(article: Omit<ArticleHistoryItem, 'id'>): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            // First, try to find existing article with same URL and user agent
            const index = store.index('date');
            const request = index.openCursor(null, 'prev');
            let foundExisting = false;

            request.onerror = () => reject(request.error);
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor && !foundExisting) {
                    const existingArticle = cursor.value;
                    // Check if URL and user agent match
                    if (existingArticle.url === article.url && existingArticle.userAgent === article.userAgent) {
                        foundExisting = true;
                        // Update existing article with new date and content
                        const updatedArticle = {
                            ...existingArticle,
                            title: article.title,
                            content: article.content,
                            date: new Date()
                        };
                        const updateRequest = cursor.update(updatedArticle);
                        updateRequest.onerror = () => reject(updateRequest.error);
                        updateRequest.onsuccess = () => resolve();
                        return;
                    }
                    cursor.continue();
                } else if (!foundExisting) {
                    // No existing article found, add new one
                    const articleWithId: ArticleHistoryItem = {
                        ...article,
                        id: `${article.url}-${Date.now()}`
                    };
                    const addRequest = store.add(articleWithId);
                    addRequest.onerror = () => reject(addRequest.error);
                    addRequest.onsuccess = () => {
                        // Keep only the last 20 articles
                        this.limitArticles(20);
                        resolve();
                    };
                }
            };
        });
    }

    async getArticles(limit: number = 20): Promise<ArticleHistoryItem[]> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('date');

            const request = index.openCursor(null, 'prev'); // Most recent first
            const results: ArticleHistoryItem[] = [];

            request.onerror = () => reject(request.error);
            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result;
                if (cursor && results.length < limit) {
                    results.push(cursor.value);
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };
        });
    }

    private async limitArticles(maxCount: number): Promise<void> {
        if (!this.db) return;

        const articles = await this.getArticles(100); // Get all articles
        if (articles.length <= maxCount) return;

        const articlesToDelete = articles.slice(maxCount);

        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        articlesToDelete.forEach(article => {
            store.delete(article.id);
        });
    }

    async clearHistory(): Promise<void> {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.clear();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }
}

export const articleHistoryDB = new ArticleHistoryDB();